import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Copy,
  Building2,
  Tag,
  Clock,
  Package,
  ShoppingCart,
  Percent,
  Truck,
  ClipboardCheck,
  Pencil,
  CheckCircle2,
  Zap,
  FileText,
} from 'lucide-react'
import { api } from '../../lib/api'
import { SpvInvoiceViewer } from '../../components/SpvInvoiceViewer'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { SearchSelect } from '../../components/ui/SearchSelect'
import { Modal } from '../../components/ui/Modal'
import { CautaAdaugaPartener } from '../../components/parteneri/CautaAdaugaPartener'
import { AddBranchModal } from '../../components/orders/AddBranchModal'
import { StatusBadge, type OrderStatus } from './ComenziPage'
import { LivrareComanda } from '../../components/orders/AwbComanda'
import { useConfirmare } from '../../components/ui/Confirmare'
import { Traseu } from '../../components/ui/Traseu'
import { DrumulComenzii, pasiTraseu } from '../../components/orders/DrumulComenzii'

interface ClientOpt {
  id: number
  name: string
  cif: string | null
  agentName?: string | null
  paymTermDays?: number | null
}
interface ProductOpt {
  id: number
  sku: string
  name: string
  vatRate: number
  listPrice: number
  priceMissing: boolean
  fromClientRule: boolean
  stock: number | null
  /** Cifra vine din lista pusa cu mana (Setari -> Comenzi), nu din EA. */
  stocManual?: boolean
  imageUrl: string | null
}

interface Line {
  productId: number
  sku: string
  name: string
  qty: number
  listPrice: number
  lineDiscountPct: number | null
  vatRate: number
  priceMissing: boolean
  stock: number | null
}

interface BranchOpt {
  id: number
  branchName: string
  address: string | null
  city: string | null
  state: string | null
  country?: string | null
  person?: string | null
  tel?: string | null
}

interface OrderDetail {
  id: number
  code: string
  status: OrderStatus
  clientId: number
  branchId: number | null
  branch: { id: number; branchName: string } | null
  clientLocation: string | null
  orderDiscountPct: string
  notes: string | null
  cod: boolean
  rejectedReason: string | null
  eaDocId: string | null
  eaError: string | null
  eaSentAt: string | null
  createdAt: string
  createdBy: { id: number; name: string } | null
  approvedBy: { id: number; name: string } | null
  /** Jurnalul comenzii, cel mai nou primul — traseul din pagina (21.10.2026). */
  auditEvents: {
    id: number
    action: string
    fromStatus: string | null
    toStatus: string | null
    reason: string | null
    createdAt: string
    actor: { id: number; name: string } | null
  }[]
  client: ClientOpt | null
  lines: {
    productId: number
    qty: number
    listPrice: string
    lineDiscountPct: string | null
    vatRate: number
    priceMissing: boolean
    product: { id: number; sku: string; name: string }
  }[]
}

const round2 = (n: number) => Math.round(n * 100) / 100
const fmt = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const CHECK_ITEMS = [
  'Am verificat stocul',
  'Datele clientului sunt corecte',
  'Produsele și cantitățile sunt corecte',
  'Discounturile aplicate sunt corecte',
  'Informațiile de livrare sunt corecte',
]

/** Familia produsului derivată din nume (primul cuvânt-cheie), pentru filtrele rapide. */
function productFamily(name: string): string {
  const first = (name.trim().split(/[\s,]+/)[0] || '').replace(/[^0-9A-Za-zĂÂÎȘȚăâîșț]/g, '')
  const w = first.toLowerCase()
  if (w === 'power') return 'Power V8'
  if (w === 'vitamina' || w === 'vitamin') return 'Vitamina D3'
  if (!first) return 'Altele'
  return first.charAt(0).toUpperCase() + first.slice(1)
}

/** Inițiale pentru avatarul de produs (fallback când nu avem poză). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase()
}

/** Avatar produs: poză (din boost4life.ro) cu fallback la inițiale. Cu poză → clic o mărește. */
function ProductAvatar({
  url,
  name,
  className = 'h-8 w-8',
  onZoom,
}: {
  url?: string | null
  name: string
  className?: string
  onZoom?: (url: string) => void
}) {
  if (url)
    return (
      <button
        type="button"
        title="Vezi poza mărită"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onZoom?.(url)
        }}
        className={`${className} shrink-0 overflow-hidden rounded-full ring-1 ring-slate-200`}
      >
        <img src={url} alt="" loading="lazy" className="h-full w-full bg-white object-cover" />
      </button>
    )
  return (
    <span
      className={`${className} flex shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-500`}
    >
      {initials(name)}
    </span>
  )
}

/** Plăcuță info în cardul Client. */
function Tile({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode
  label: string
  value: string
  accent?: boolean
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-b4l-border bg-slate-50 px-3 py-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-b4l-blue shadow-sm">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-b4l-text-muted">{label}</div>
        <div className={`truncate text-sm font-semibold ${accent ? 'text-b4l-blue' : ''}`}>
          {value}
        </div>
      </div>
    </div>
  )
}

export function ComandaEditPage() {
  const { id } = useParams()
  const isNew = id === 'nou'
  const orderId = isNew ? null : Number(id)
  const navigate = useNavigate()
  const location = useLocation()
  const { confirma, cere } = useConfirmare()
  const qc = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canApprove = permissions.includes('orders:approve')
  const canWrite = permissions.includes('orders:write')
  // Poate declansa o actiune de modificare (scriere SAU aprobare). Userii read-only nu.
  const canMutate = canWrite || canApprove
  // Doar adminul poate modifica prețul; ceilalți doar discountul (cerință 07.2026).
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin')

  // „+ Comandă nouă" din fisa clientului vine cu `?clientId=` — clientul e deja ales.
  const [cautare] = useSearchParams()
  const [clientId, setClientId] = useState<number | null>(() =>
    isNew ? Number(cautare.get('clientId')) || null : null,
  )
  const [branchId, setBranchId] = useState<number | null>(null)
  const [clientLocation, setClientLocation] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [addBranchOpen, setAddBranchOpen] = useState(false)
  const [orderDiscountPct, setOrderDiscountPct] = useState(0)
  const [notes, setNotes] = useState('')
  const [cod, setCod] = useState(false)
  const [lines, setLines] = useState<Line[]>([])
  const [status, setStatus] = useState<OrderStatus>('ciorna')
  const [code, setCode] = useState('')
  const [rejectedReason, setRejectedReason] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [productSearch, setProductSearch] = useState('')
  const [stockOnly, setStockOnly] = useState(false)
  const [familyFilter, setFamilyFilter] = useState<string | null>(null)
  const [zoomUrl, setZoomUrl] = useState<string | null>(null)
  const [spvViewer, setSpvViewer] = useState<{ inboundId: number; docId: string | null } | null>(
    null,
  )
  // Comandă nouă începe în editare; o comandă salvată se deschide în Revizuire.
  const [mode, setMode] = useState<'edit' | 'review'>(isNew ? 'edit' : 'review')
  const [checks, setChecks] = useState<Set<string>>(new Set())
  // Padding jos dinamic = inaltimea barei sticky (variaza cu nr. de butoane pe status)
  const bottomBarRef = useRef<HTMLDivElement>(null)
  const [barH, setBarH] = useState(0)
  useEffect(() => {
    const el = bottomBarRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setBarH(el.offsetHeight))
    ro.observe(el)
    setBarH(el.offsetHeight)
    return () => ro.disconnect()
  }, [])

  /*
    Cand a fost citit stocul din EA, si un buton care il reciteste.

    Eticheta rosie „Stoc 20 · insuficient" nu spunea DIN CE MOMENT e cifra, iar intrebarea care
    urmeaza mereu e „e stocul vechi sau chiar n-am marfa?". Pe 22.09.2026 raspunsul a cerut o
    sesiune SSH pe server si trei interogari catre EA, ca sa se afle ca marfa era prinsa
    intr-un document deschis in editare in ERP — iar CRM-ul avea dreptate.

    La deschiderea ecranului se cere `sync-stock-if-stale`: are TTL si protectie la concurenta pe
    server, deci nu bate EA-ul la fiecare deschidere, dar intoarce ÎNTOTDEAUNA momentul citirii.
  */
  const [stocLaData, setStocLaData] = useState<string | null>(null)
  useEffect(() => {
    api
      .post<{ result: { synced: boolean; asOf: string | null } }>('/orders/sync-stock-if-stale')
      .then((r) => {
        setStocLaData(r.data?.result?.asOf ?? null)
        if (r.data?.result?.synced) qc.invalidateQueries({ queryKey: ['client-products'] })
      })
      .catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* Recitirea fortata: pentru clipa de dupa ce s-a inchis documentul in EA. Ocoleste TTL-ul. */
  const resyncStoc = useMutation({
    mutationFn: async () =>
      (await api.post<{ result: { updated: number; total: number } }>('/orders/sync-stock')).data,
    onSuccess: async (r) => {
      setStocLaData(new Date().toISOString())
      await qc.invalidateQueries({ queryKey: ['client-products'] })
      toast.success(`Stoc recitit din ExpertAccounts: ${r.result.total} articole.`)
    },
    onError: () => toast.error('Nu am putut reciti stocul din ExpertAccounts.'),
  })

  const { data: clients = [] } = useQuery({
    queryKey: ['orders-clients'],
    queryFn: async () => (await api.get<{ clients: ClientOpt[] }>('/orders/clients')).data.clients,
  })
  const { data: products = [] } = useQuery({
    queryKey: ['client-products', clientId],
    queryFn: async () =>
      (await api.get<{ products: ProductOpt[] }>(`/orders/client-products?clientId=${clientId}`))
        .data.products,
    enabled: clientId != null,
  })
  // Buton „Reîmprospătează prețuri”: forțează reconcilierea din EA (ocolește cache-ul),
  // pentru când tocmai s-a schimbat un preț în EA și vrei să-l vezi imediat în comandă.
  const refreshPricesMut = useMutation({
    mutationFn: async () => api.post(`/orders/clients/${clientId}/refresh-prices`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['client-products', clientId] })
      toast.success('Prețuri reîmprospătate din ExpertAccounts.')
    },
    onError: () => toast.error('Nu am putut reîmprospăta prețurile din EA.'),
  })
  const { data: branches = [] } = useQuery({
    queryKey: ['client-branches', clientId],
    queryFn: async () =>
      (await api.get<{ branches: BranchOpt[] }>(`/orders/clients/${clientId}/branches`)).data
        .branches,
    enabled: clientId != null,
  })
  // „Ultima comandă" a clientului (CRM trimise, ERP, facturi), fără comanda deschisă acum.
  const { data: eaActivity } = useQuery({
    queryKey: ['client-ea-activity', clientId, orderId],
    queryFn: async () =>
      (
        await api.get<{
          activity: {
            ultimaComanda: { data: string; sursa: 'crm' | 'erp' | 'factura' } | null
            sales30d: number
          }
        }>(`/orders/clients/${clientId}/activity`, {
          params: orderId ? { faraComanda: orderId } : undefined,
        })
      ).data.activity,
    enabled: clientId != null,
  })
  // Refresh ON-ACCESS: la selectarea clientului, cerem EA ultima lui comandă (doar partenerul lui)
  // și reîmprospătăm activitatea. Dacă EA e indisponibil, rămâne pe datele din sync-ul zilnic.
  useEffect(() => {
    if (clientId == null) return
    api
      .post<{ lastOrderAt: string | null }>(`/orders/clients/${clientId}/refresh-last-order`)
      .then((r) => {
        if (r.data?.lastOrderAt)
          qc.invalidateQueries({ queryKey: ['client-ea-activity', clientId] })
      })
      .catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId])
  // Factura SPV a comenzii (doar pentru comenzi trimise în EA/facturate).
  const { data: spvInvoice, isFetched: spvChecked } = useQuery({
    queryKey: ['order-spv', orderId],
    queryFn: async () =>
      (
        await api.get<{
          invoice: { inboundId: number; docId: string | null; issueDate: string | null } | null
        }>(`/orders/${orderId}/spv-invoice`)
      ).data.invoice,
    enabled: orderId != null && status === 'trimisa_ea',
  })
  const { data: settings } = useQuery({
    queryKey: ['order-settings'],
    queryFn: async () =>
      (await api.get<{ settings: { discountApprovalThresholdPct: string } }>('/orders/settings'))
        .data.settings,
  })
  const { data: detail } = useQuery({
    queryKey: ['order', orderId],
    queryFn: async () => (await api.get<{ order: OrderDetail }>(`/orders/${orderId}`)).data.order,
    enabled: !isNew,
  })

  useEffect(() => {
    if (!detail) return
    setClientId(detail.clientId)
    setBranchId(detail.branchId ?? null)
    setClientLocation(detail.clientLocation ?? '')
    setOrderDiscountPct(Number(detail.orderDiscountPct))
    setNotes(detail.notes ?? '')
    setCod(detail.cod ?? false)
    setStatus(detail.status)
    setCode(detail.code)
    setRejectedReason(detail.rejectedReason)
    setLines(
      detail.lines.map((l) => ({
        productId: l.productId,
        sku: l.product.sku,
        name: l.product.name,
        qty: l.qty,
        listPrice: Number(l.listPrice),
        lineDiscountPct: l.lineDiscountPct == null ? null : Number(l.lineDiscountPct),
        vatRate: l.vatRate,
        priceMissing: l.priceMissing,
        stock: null,
      })),
    )
  }, [detail])

  // Comanda se poate modifica/anula/sterge pana ajunge in ERP. Bariera nu e statusul, ci
  // documentul din EA: la un esec partial de trimitere el poate exista desi statusul a ramas
  // „aprobata" (cazul CMD-2026-0086). Aceleasi reguli sunt aplicate si de API.
  const inEa = detail?.eaDocId != null || status === 'trimisa_ea'
  const beforeEa =
    !inEa &&
    (status === 'ciorna' ||
      status === 'respinsa' ||
      status === 'asteapta_aprobare' ||
      status === 'aprobata' ||
      status === 'eroare_ea')
  const editable = canWrite && (isNew || beforeEa)
  const canSubmit = status === 'ciorna' || status === 'respinsa'
  const threshold = settings ? Number(settings.discountApprovalThresholdPct) : 15

  function toggleSelected(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function addSelected() {
    if (selectedIds.size === 0) return
    const toAdd = products.filter(
      (p) => selectedIds.has(p.id) && !lines.some((l) => l.productId === p.id),
    )
    setLines((prev) => [
      ...prev,
      ...toAdd.map((p) => ({
        productId: p.id,
        sku: p.sku,
        name: p.name,
        qty: 1,
        listPrice: p.listPrice,
        lineDiscountPct: null,
        vatRate: p.vatRate,
        priceMissing: p.priceMissing,
        stock: p.stock,
      })),
    ])
    setSelectedIds(new Set())
    setProductSearch('')
  }

  // Familiile prezente în catalogul clientului (pentru chips-uri), în ordine alfabetică.
  const families = Array.from(new Set(products.map((p) => productFamily(p.name)))).sort((a, b) =>
    a.localeCompare(b, 'ro'),
  )

  const availableProducts = products
    .filter((p) => !lines.some((l) => l.productId === p.id))
    .filter((p) => !stockOnly || (p.stock != null && p.stock > 0))
    .filter((p) => !familyFilter || productFamily(p.name) === familyFilter)
    .filter((p) => {
      const q = productSearch.trim().toLowerCase()
      return !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)
    })

  const imgByProduct = new Map(products.map((p) => [p.id, p.imageUrl]))
  const stockByProduct = new Map(products.map((p) => [p.id, p.stock]))

  function updateLine(idx: number, patch: Partial<Line>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)))
  }
  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx))
  }

  const computed = useMemo(() => {
    let noVat = 0
    let vat = 0
    let maxDisc = 0
    const rows = lines.map((l) => {
      const disc = l.lineDiscountPct ?? orderDiscountPct
      maxDisc = Math.max(maxDisc, disc)
      const eff = round2(l.listPrice * (1 - disc / 100))
      const lineNoVat = round2(l.qty * eff)
      noVat += lineNoVat
      vat += round2((lineNoVat * l.vatRate) / 100)
      return { eff, lineNoVat, disc }
    })
    return { rows, noVat: round2(noVat), vat: round2(vat), withVat: round2(noVat + vat), maxDisc }
  }, [lines, orderDiscountPct])

  const willNeedApproval = computed.maxDisc > threshold

  // Info client (derivat din datele deja aduse)
  const hasClientPrices = products.some((p) => p.fromClientRule)
  // „Ultima comandă": aceeași socoteală ca în Tabloul de bord (CRM trimise, ERP, facturi).
  const lastOrderAt = eaActivity?.ultimaComanda?.data ?? null

  const discState = willNeedApproval
    ? { label: 'Peste prag → aprobare', cls: 'bg-amber-100 text-amber-800' }
    : computed.maxDisc > 0 && computed.maxDisc >= threshold * 0.8
      ? { label: 'Aproape de limită', cls: 'bg-amber-50 text-amber-700' }
      : { label: 'OK', cls: 'bg-emerald-100 text-emerald-700' }

  // Date derivate pentru vederea de Revizuire
  const clientName = detail?.client?.name ?? clients.find((c) => c.id === clientId)?.name ?? '—'
  const selectedBranch = branches.find((b) => b.id === branchId) ?? null
  const punctLucruLabel = selectedBranch
    ? [selectedBranch.branchName, selectedBranch.city, selectedBranch.state]
        .filter(Boolean)
        .join(' · ')
    : clientLocation || 'Adresa partenerului (implicit)'
  const livrareAdresa = selectedBranch
    ? [selectedBranch.address, selectedBranch.city, selectedBranch.state, selectedBranch.country]
        .filter(Boolean)
        .join(', ')
    : clientLocation || '—'
  const livrareContact =
    selectedBranch && (selectedBranch.person || selectedBranch.tel)
      ? [selectedBranch.person, selectedBranch.tel].filter(Boolean).join(' · ')
      : null

  function buildPayload() {
    return {
      clientId,
      branchId: branchId ?? null,
      clientLocation: branchId ? undefined : clientLocation || null,
      orderDiscountPct,
      notes: notes || null,
      cod,
      lines: lines.map((l) => ({
        productId: l.productId,
        qty: l.qty,
        listPrice: l.listPrice,
        lineDiscountPct: l.lineDiscountPct,
      })),
    }
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      if (clientId == null) throw new Error('Selectează clientul')
      if (isNew)
        return (await api.post<{ order: { id: number } }>('/orders', buildPayload())).data.order
      const res = (
        await api.patch<{ order: { id: number }; sentBackToApproval?: boolean }>(
          `/orders/${orderId}`,
          buildPayload(),
        )
      ).data
      return { ...res.order, sentBackToApproval: res.sentBackToApproval }
    },
    onSuccess: (o) => {
      if ((o as { sentBackToApproval?: boolean }).sentBackToApproval)
        toast.success('Comandă salvată — discountul depășește pragul, a fost retrimisă la aprobare')
      else toast.success('Comandă salvată')
      qc.invalidateQueries({ queryKey: ['orders'] })
      if (isNew) navigate(`/operational/comenzi/${o.id}`, { replace: true })
      else {
        qc.invalidateQueries({ queryKey: ['order', orderId] })
        setMode('review')
      }
    },
    onError: (e: unknown) => toast.error((e as Error).message || 'Eroare la salvare'),
  })

  const duplicateMut = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ order: { id: number }; produseScoase?: string[] }>(
          `/orders/${orderId}/duplicate`,
        )
      ).data,
    onSuccess: ({ order: o, produseScoase }) => {
      if (produseScoase?.length)
        toast.warning(
          `Comandă duplicată, fără ${produseScoase.map((n) => `„${n}”`).join(', ')}: nu mai ${produseScoase.length === 1 ? 'e' : 'sunt'} pe lista clientului.`,
          { duration: 10_000 },
        )
      else toast.success('Comandă duplicată')
      qc.invalidateQueries({ queryKey: ['orders'] })
      navigate(`/operational/comenzi/${o.id}`)
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message || 'Eroare la duplicare',
      ),
  })

  const sendEaMut = useMutation({
    mutationFn: async (verificatInEa?: boolean) =>
      (
        await api.post<{ order: { eaDocId: string | null; regasitaInEa?: boolean } }>(
          `/orders/${orderId}/send-ea`,
          verificatInEa ? { verificatInEa: true } : {},
          /*
            NU 15 secunde, cat au celelalte apeluri. La reincercare serverul cauta intai comanda in
            EA, pagina cu pagina, iar cand EA e lent asta dureaza peste un minut. Pe 12.10.2026
            ecranul arata „Trimitere EA esuata" dupa 15 s, iar serverul gasea comanda si o marca
            trimisa in fundal — omul credea ca n-a mers.
          */
          { timeout: 120_000 },
        )
      ).data,
    onSuccess: (r) => {
      // La reincercare, CRM-ul intreaba intai EA: comanda poate fi intrat deja, fara raspuns.
      if (r.order.regasitaInEa) {
        toast.success(
          `Comanda era deja în ExpertAccounts (nr. ${r.order.eaDocId}) — marcată ca trimisă, nu s-a retrimis.`,
          { duration: 10_000 },
        )
      } else {
        toast.success('Comandă trimisă în ExpertAccounts')
      }
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['order', orderId] })
    },
    onError: (e: unknown) => {
      const err = (
        e as {
          response?: {
            data?: { error?: { message?: string; fields?: Record<string, string[]> } }
          }
        }
      ).response?.data?.error
      /*
        EA nu poate fi intrebat acum, iar trimiterea anterioara a ramas fara raspuns: comanda poate
        exista deja acolo. Se retrimite doar dupa ce omul a verificat in EA.
      */
      if (err?.fields?.neverificatInEa) {
        void confirma({
          titlu: 'Comanda poate exista deja în ExpertAccounts',
          mesaj: `${err.message} Retrimite doar dacă ai verificat în ExpertAccounts că NU există — altfel se dublează.`,
          buton: 'Am verificat, retrimite',
          pericol: true,
        }).then((da) => da && sendEaMut.mutate(true))
        return
      }
      /*
        Fara raspuns de la server (am renuntat noi sa asteptam, sau proxy-ul a taiat cererea):
        serverul poate inca lucra. Nu spunem „esuat" — spunem sa se uite din nou.
      */
      const faraRaspuns =
        !(e as { response?: unknown }).response ||
        [502, 504].includes((e as { response?: { status?: number } }).response?.status ?? 0)
      if (faraRaspuns) {
        toast.warning(
          'ExpertAccounts răspunde greu, iar trimiterea e în curs. Reîncarcă pagina peste un minut: dacă starea e „Trimisă în EA”, comanda a ajuns. Nu apăsa din nou până atunci.',
          { duration: 20_000 },
        )
        qc.invalidateQueries({ queryKey: ['order', orderId] })
        return
      }
      toast.error(err?.message || 'Trimitere EA eșuată')
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => (await api.delete(`/orders/${orderId}`)).data,
    onSuccess: () => {
      toast.success('Comandă ștearsă')
      qc.invalidateQueries({ queryKey: ['orders'] })
      navigate('/operational/comenzi')
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message || 'Eroare la ștergere',
      ),
  })

  const actionMut = useMutation({
    mutationFn: async (vars: { action: string; reason?: string }) =>
      (
        await api.post(
          `/orders/${orderId}/${vars.action}`,
          vars.reason ? { reason: vars.reason } : undefined,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['order', orderId] })
      toast.success('Actualizat')
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message || 'Eroare',
      ),
  })

  /** Grupul de butoane de acțiune — refolosit în panoul lateral (desktop) și bara de jos (mobil). */
  function renderActions() {
    return (
      <>
        {mode === 'review' && editable && (
          <Button variant="secondary" onClick={() => setMode('edit')}>
            <Pencil size={16} /> Editează
          </Button>
        )}
        {mode === 'edit' && editable && (
          <>
            <Button onClick={() => saveMut.mutate()} loading={saveMut.isPending}>
              Salvează
            </Button>
            {!isNew && (
              <Button variant="ghost" onClick={() => setMode('review')}>
                Revizuire
              </Button>
            )}
          </>
        )}
        {!isNew && canSubmit && mode === 'review' && canMutate && (
          <Button
            variant="primary"
            disabled={lines.length === 0 || checks.size < CHECK_ITEMS.length}
            title={
              checks.size < CHECK_ITEMS.length
                ? 'Bifează tot checklist-ul înainte de trimitere'
                : undefined
            }
            onClick={async () => {
              if (editable) await saveMut.mutateAsync()
              // Aprobatorii (admin / orders:approve) sar peste pasul de aprobare:
              // submit + approve dintr-un pas -> comanda ajunge direct „aprobată".
              if (canApprove) {
                if (
                  !(await confirma({
                    titlu: `Aprobi comanda ${code}?`,
                    mesaj: 'Verifică produsele și discountul.',
                    buton: 'Aprobă',
                  }))
                )
                  return
                // Comenzile sub prag se aproba automat la submit; aprobam explicit DOAR pe
                // cele care raman „asteapta_aprobare" (discount peste prag). Altfel al doilea
                // apel ar da eroare „comanda nu asteapta aprobare".
                const res = await actionMut.mutateAsync({ action: 'submit' })
                const submitted = (res as { order?: { status?: string } })?.order
                if (submitted?.status === 'asteapta_aprobare') {
                  actionMut.mutate({ action: 'approve' })
                }
              } else {
                actionMut.mutate({ action: 'submit' })
              }
            }}
          >
            {canApprove ? 'Aprobă comanda' : 'Trimite spre aprobare'}
          </Button>
        )}
        {!isNew && status === 'asteapta_aprobare' && canApprove && (
          <>
            <Button
              onClick={async () => {
                if (
                  await confirma({
                    titlu: `Aprobi comanda ${code}?`,
                    mesaj: 'Verifică întâi produsele și discountul.',
                    buton: 'Aprobă',
                  })
                )
                  actionMut.mutate({ action: 'approve' })
              }}
            >
              Aprobă
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                const reason = await cere({
                  titlu: `Respingi comanda ${code}?`,
                  mesaj: 'Agentul vede motivul pe comandă și o poate corecta și retrimite.',
                  eticheta: 'Motivul respingerii',
                  buton: 'Respinge',
                  obligatoriu: true,
                  lung: true,
                })
                if (reason) actionMut.mutate({ action: 'reject', reason })
              }}
            >
              Respinge
            </Button>
          </>
        )}
        {/* Doar din Revizuire: în „edit" pot exista modificări nesalvate care n-ar ajunge în EA. */}
        {!isNew &&
          mode === 'review' &&
          (status === 'aprobata' || status === 'eroare_ea') &&
          canMutate && (
            <Button
              onClick={async () => {
                if (
                  await confirma({
                    titlu: `Trimiți comanda ${code} în ExpertAccounts?`,
                    mesaj: 'După trimitere comanda nu se mai poate modifica din CRM.',
                    buton: 'Trimite în EA',
                  })
                )
                  sendEaMut.mutate(false)
              }}
              loading={sendEaMut.isPending}
            >
              {status === 'eroare_ea' ? 'Reîncearcă trimiterea EA' : 'Trimite în EA'}
            </Button>
          )}
        {!isNew &&
          status === 'trimisa_ea' &&
          (spvInvoice ? (
            <Button
              variant="secondary"
              onClick={() =>
                setSpvViewer({ inboundId: spvInvoice.inboundId, docId: spvInvoice.docId })
              }
              title="Afișează factura din SPV"
            >
              <FileText size={16} />{' '}
              {spvInvoice.docId ? `Vezi factura ${spvInvoice.docId}` : 'Vezi factura SPV'}
            </Button>
          ) : spvChecked ? (
            <span className="self-center text-xs text-b4l-text-muted">
              Factura încă nu apare în SPV
            </span>
          ) : null)}
        {/* Eroare EA o anuleaza doar un admin: poate exista deja in ERP (ca pe server). */}
        {!isNew && beforeEa && canMutate && (status !== 'eroare_ea' || canApprove) && (
          <Button
            variant="secondary"
            onClick={() => {
              void confirma({
                titlu: `Anulezi comanda ${code}?`,
                mesaj: 'Comanda rămâne în listă, ca anulată.',
                buton: 'Anulează comanda',
                pericol: true,
              }).then((da) => da && actionMut.mutate({ action: 'cancel' }))
            }}
          >
            Anulează
          </Button>
        )}
        {/* „Eroare EA" e protejata ca o comanda trimisa: o sterge doar un admin (ca pe server). */}
        {!isNew && (inEa || status === 'eroare_ea' ? canApprove : canMutate) && (
          <Button
            variant="danger"
            onClick={() => setConfirmDelete(true)}
            loading={deleteMut.isPending}
          >
            Șterge
          </Button>
        )}
      </>
    )
  }

  return (
    <div
      className="space-y-4 overflow-x-clip pb-24 lg:overflow-x-visible lg:pb-4"
      style={{ paddingBottom: barH ? barH + 16 : undefined }}
    >
      <header className="grid gap-3">
        {/*
          Inapoi la lista ASA CUM ERA (filtrele stau in adresa ei): pasul inapoi din istoric doar
          cand s-a venit chiar din lista (semnul `dinLista`); din fisa clientului, din Rambursuri
          sau dupa o comanda noua, lista de la zero — eticheta spune „Comenzi".
        */}
        <button
          type="button"
          onClick={() =>
            (location.state as { dinLista?: boolean } | null)?.dinLista
              ? navigate(-1)
              : navigate('/operational/comenzi')
          }
          className="inline-flex w-fit items-center gap-1.5 text-sm text-b4l-blue-dark hover:underline"
        >
          <ArrowLeft size={15} /> Comenzi
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid gap-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-2xl font-semibold text-b4l-text">
                {isNew ? 'Comandă nouă' : code}
              </h1>
              {!isNew && <StatusBadge status={status} />}
            </div>
            {!isNew && detail && (
              <p className="text-sm text-b4l-text-muted">
                {detail.client ? (
                  <Link
                    to={`/clienti/${detail.client.id}`}
                    className="text-b4l-blue-dark hover:underline"
                  >
                    {detail.client.name}
                  </Link>
                ) : (
                  '—'
                )}
                {detail.clientLocation ? ` · ${detail.clientLocation}` : ''}
                {detail.client?.agentName ? ` · agent ${detail.client.agentName}` : ''}
                {detail.createdBy
                  ? ` · creată de ${detail.createdBy.name}, ${new Date(detail.createdAt).toLocaleDateString('ro-RO')}`
                  : ''}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* In Revizuire, actiunile stau aici; la editare, in coloana din dreapta, langa rezumat. */}
            {!isNew && mode === 'review' && (
              <div className="hidden flex-wrap gap-2 lg:flex">{renderActions()}</div>
            )}
            {!isNew && (
              <Button
                variant="secondary"
                onClick={() => duplicateMut.mutate()}
                loading={duplicateMut.isPending}
              >
                <Copy size={16} /> Duplică
              </Button>
            )}
          </div>
        </div>
        {!isNew && detail && orderId != null && (
          <DrumulComenzii
            orderId={orderId}
            status={status}
            createdAt={detail.createdAt}
            eaSentAt={detail.eaSentAt}
            evenimente={detail.auditEvents ?? []}
            factura={spvInvoice}
          />
        )}
      </header>

      {rejectedReason && (status === 'respinsa' || status === 'ciorna') && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          Respinsă: {rejectedReason} — ajusteaz-o și retrimite.
        </div>
      )}
      {(status === 'aprobata' || status === 'eroare_ea') && mode === 'edit' && (
        <div className="rounded-lg border border-b4l-amber bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
          Comanda e aprobată, dar nu a fost încă trimisă în ExpertAccounts — o poți modifica. Dacă
          discountul trece de pragul de {threshold}%, revine automat la aprobare.
        </div>
      )}
      {status === 'trimisa_ea' && detail?.eaDocId && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          Trimisă în ExpertAccounts · document: <b>{detail.eaDocId}</b>
        </div>
      )}
      {/* AWB-urile comenzii — doar dupa ERP: o ciorna n-are colet (Victor, 21.10.2026). */}
      {orderId != null && inEa && <LivrareComanda orderId={orderId} />}
      {status === 'eroare_ea' && detail?.eaError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Eroare la trimiterea în EA: {detail.eaError}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_360px] lg:items-start">
        {/* Coloana stângă (în Revizuire: 2 sub-coloane masonry + panoul Rezumat = 3 coloane) */}
        <div className={mode === 'edit' ? 'space-y-4' : 'lg:col-span-2'}>
          {mode === 'edit' ? (
            <>
              <Card>
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
                    <Building2 size={18} />
                  </span>
                  <div>
                    <h2 className="text-base font-semibold tracking-tight">
                      <span className="text-b4l-blue">1</span> · Client și punct de lucru
                    </h2>
                    <p className="text-xs text-b4l-text-muted">
                      Alege clientul și punctul de lucru pentru livrare.
                    </p>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-sm text-gray-600">Client</span>
                    {/*
                      Căutarea comună de parteneri: clienții tăi, apoi ExpertAccounts, apoi ANAF,
                      și „+ Adaugă partener nou" la final (spec parteneri comuni, §5).
                    */}
                    <CautaAdaugaPartener
                      baza="/orders"
                      toti={clients.map((c) => ({ id: c.id, nume: c.name }))}
                      value={
                        clientId != null
                          ? {
                              id: clientId,
                              nume: clients.find((c) => c.id === clientId)?.name ?? '…',
                            }
                          : null
                      }
                      onChange={async (p) => {
                        // Un client adus din EA sau creat acum nu e încă în lista încărcată.
                        if (p && !clients.some((c) => c.id === p.id))
                          await qc.invalidateQueries({ queryKey: ['orders-clients'] })
                        setClientId(p?.id ?? null)
                        setBranchId(null)
                      }}
                      disabled={!editable}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 flex items-center justify-between text-sm text-gray-600">
                      <span>Punct de lucru{branches.length > 0 ? '' : ' (opțional)'}</span>
                      {editable && clientId != null && (
                        <button
                          type="button"
                          className="text-xs font-medium text-b4l-primary hover:underline"
                          onClick={() => setAddBranchOpen(true)}
                        >
                          + Punct nou
                        </button>
                      )}
                    </span>
                    {branches.length > 0 ? (
                      <SearchSelect
                        options={branches.map((b) => ({
                          value: b.id,
                          label: [b.branchName, b.city, b.state].filter(Boolean).join(' · '),
                        }))}
                        value={branchId}
                        onChange={setBranchId}
                        disabled={!editable}
                        placeholder="Adresa partenerului (implicit)"
                      />
                    ) : (
                      <input
                        className="w-full rounded-md border border-gray-400 bg-white px-3 py-2 shadow-sm"
                        value={clientLocation}
                        disabled={!editable}
                        onChange={(e) => setClientLocation(e.target.value)}
                      />
                    )}
                  </label>
                </div>
                {clientId != null && (
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <Tile
                      icon={<Tag size={16} />}
                      label="Tip preț"
                      value={hasClientPrices ? 'Preț client' : 'Preț listă'}
                      accent={hasClientPrices}
                    />
                    <Tile
                      icon={<Clock size={16} />}
                      label="Ultima comandă"
                      value={lastOrderAt ? new Date(lastOrderAt).toLocaleDateString('ro-RO') : '—'}
                    />
                  </div>
                )}
              </Card>

              <Card>
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
                    <Package size={18} />
                  </span>
                  <div className="flex-1">
                    <h2 className="text-base font-semibold tracking-tight">
                      <span className="text-b4l-blue">2</span> · Produse
                    </h2>
                    <p className="text-xs text-b4l-text-muted">
                      Adaugă articolele, cantitățile și discountul.
                    </p>
                  </div>
                  {editable && clientId != null && (
                    <Button
                      variant="secondary"
                      onClick={() => refreshPricesMut.mutate()}
                      loading={refreshPricesMut.isPending}
                      title="Reia prețurile din ExpertAccounts (după ce ai modificat o regulă acolo)"
                    >
                      ↻ Reîmprospătează prețuri
                    </Button>
                  )}
                </div>

                {editable && (
                  <div className="mb-3 rounded-lg border border-gray-200 p-3">
                    {clientId == null ? (
                      <p className="text-sm text-gray-500">Alege întâi clientul.</p>
                    ) : (
                      <>
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <input
                            className="min-w-[180px] flex-1 rounded-md border border-gray-400 bg-white px-3 py-2 shadow-sm text-sm"
                            placeholder="Caută produs…"
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                          />
                          <Button onClick={addSelected} disabled={selectedIds.size === 0}>
                            Adaugă selectate ({selectedIds.size})
                          </Button>
                        </div>
                        <div className="mb-2 flex gap-1.5">
                          {(
                            [
                              { key: false, label: 'Toate' },
                              { key: true, label: 'În stoc' },
                            ] as const
                          ).map((chip) => (
                            <button
                              key={String(chip.key)}
                              type="button"
                              onClick={() => setStockOnly(chip.key)}
                              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                                stockOnly === chip.key
                                  ? 'bg-b4l-blue text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {chip.label}
                            </button>
                          ))}
                        </div>
                        {families.length > 1 && (
                          <div className="mb-2 flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => setFamilyFilter(null)}
                              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                                familyFilter === null
                                  ? 'bg-b4l-blue text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              Toate familiile
                            </button>
                            {families.map((f) => (
                              <button
                                key={f}
                                type="button"
                                onClick={() => setFamilyFilter(familyFilter === f ? null : f)}
                                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                                  familyFilter === f
                                    ? 'bg-b4l-blue text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                              >
                                {f}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="max-h-64 space-y-0.5 overflow-y-auto">
                          {availableProducts.length === 0 ? (
                            <p className="px-2 py-1 text-sm text-gray-400">
                              {products.length === 0
                                ? 'Clientul nu are produse cu preț.'
                                : 'Niciun produs disponibil pentru filtrul curent.'}
                            </p>
                          ) : (
                            availableProducts.map((p) => (
                              <label
                                key={p.id}
                                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-50"
                              >
                                <input
                                  type="checkbox"
                                  className="shrink-0"
                                  checked={selectedIds.has(p.id)}
                                  onChange={() => toggleSelected(p.id)}
                                />
                                <ProductAvatar url={p.imageUrl} name={p.name} onZoom={setZoomUrl} />
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm">
                                    {p.name} <span className="text-gray-400">({p.sku})</span>
                                  </div>
                                  {/* Stoc + preț sub denumire — vizibile și pe telefon */}
                                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                    <span
                                      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                        p.stock == null
                                          ? 'bg-slate-100 text-slate-400'
                                          : p.stock <= 0
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-emerald-100 text-emerald-700'
                                      }`}
                                      title={
                                        p.stocManual
                                          ? 'Stoc pus cu mâna în Setări → Comenzi, nu citit din EA'
                                          : 'Stoc disponibil, din ExpertAccounts'
                                      }
                                    >
                                      {p.stock == null ? 'stoc —' : `Stoc ${p.stock}`}
                                    </span>
                                    {/*
                                      Cifra pusa cu mana se vede ca atare. Altfel, cand EA si
                                      lista manuala nu sunt de acord, nimeni nu stie pe care o
                                      citeste — iar in septembrie 2026 EA a stat cinci zile cu
                                      API-ul de stoc inchis, fara ca ecranul sa spuna ceva.
                                    */}
                                    {p.stocManual && (
                                      <span
                                        className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
                                        title="Cantitatea e pusă cu mâna, nu vine din ExpertAccounts"
                                      >
                                        manual
                                      </span>
                                    )}
                                    <span className="text-sm font-semibold text-gray-700">
                                      {p.priceMissing ? 'fără preț' : `${fmt(p.listPrice)} lei`}
                                    </span>
                                    {p.fromClientRule && (
                                      <span className="rounded bg-blue-100 px-1 text-[10px] text-blue-700">
                                        preț client
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </label>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {lines.length === 0 ? (
                  <p className="text-sm text-gray-500">Nicio linie.</p>
                ) : (
                  <>
                    {/* Mobil + tabletă (sub lg): card-uri */}
                    <div className="space-y-2 lg:hidden">
                      {lines.map((l, idx) => (
                        <div key={l.productId} className="rounded-lg border border-gray-200 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex min-w-0 items-center gap-2.5">
                              <ProductAvatar
                                url={imgByProduct.get(l.productId)}
                                name={l.name}
                                className="h-9 w-9"
                                onZoom={setZoomUrl}
                              />
                              <div className="min-w-0">
                                <div className="font-medium">{l.name}</div>
                                <div className="text-xs text-gray-500">{l.sku}</div>
                              </div>
                            </div>
                            {editable && (
                              <button
                                className="shrink-0 text-sm text-red-600"
                                onClick={() => removeLine(idx)}
                              >
                                Șterge
                              </button>
                            )}
                          </div>
                          {l.priceMissing && (
                            <div className="mt-1 inline-block rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                              preț lipsă — completează manual
                            </div>
                          )}
                          {l.stock != null && l.qty > l.stock && (
                            <div className="mt-1 inline-block rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
                              peste stoc (disponibil {l.stock})
                            </div>
                          )}
                          <div className="mt-2 grid grid-cols-3 gap-2 text-sm [&>label]:min-w-0">
                            <label>
                              <span className="text-xs text-gray-500">Cant.</span>
                              <input
                                type="number"
                                min={1}
                                className="w-full rounded border px-2 py-1"
                                value={l.qty}
                                disabled={!editable}
                                onChange={(e) =>
                                  updateLine(idx, { qty: Math.max(1, Number(e.target.value)) })
                                }
                              />
                            </label>
                            <label>
                              <span className="text-xs text-gray-500">Preț</span>
                              <input
                                type="number"
                                step="0.01"
                                className={`w-full rounded border px-2 py-1 ${
                                  !isAdmin ? 'cursor-not-allowed bg-gray-100 text-gray-500' : ''
                                }`}
                                value={l.listPrice}
                                disabled={!editable || !isAdmin}
                                title={
                                  !isAdmin
                                    ? 'Doar adminul poate modifica prețul. Poți ajusta doar discountul.'
                                    : undefined
                                }
                                onChange={(e) =>
                                  updateLine(idx, { listPrice: Number(e.target.value) })
                                }
                              />
                            </label>
                            <label>
                              <span className="text-xs text-gray-500">Disc.%</span>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                className="w-full rounded border px-2 py-1"
                                value={l.lineDiscountPct ?? ''}
                                placeholder={String(orderDiscountPct)}
                                disabled={!editable}
                                onChange={(e) =>
                                  updateLine(idx, {
                                    lineDiscountPct:
                                      e.target.value === '' ? null : Number(e.target.value),
                                  })
                                }
                              />
                            </label>
                          </div>
                          <div className="mt-2 text-right text-sm font-semibold">
                            {fmt(computed.rows[idx]?.lineNoVat ?? 0)} lei
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop (lg+): tabel */}
                    <div className="hidden overflow-x-auto lg:block">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-gray-500">
                            <th className="py-2 pr-3">Produs</th>
                            <th className="py-2 pr-3 text-right">Cant.</th>
                            <th className="py-2 pr-3 text-right">Preț listă</th>
                            <th className="py-2 pr-3 text-right">Disc.%</th>
                            <th className="py-2 pr-3 text-right">Preț efectiv</th>
                            <th className="py-2 pr-3 text-right">Valoare</th>
                            <th className="py-2 pr-3">TVA</th>
                            {editable && <th />}
                          </tr>
                        </thead>
                        <tbody>
                          {lines.map((l, idx) => (
                            <tr key={l.productId} className="border-b">
                              <td className="py-2 pr-3">
                                <div className="flex items-center gap-2.5">
                                  <ProductAvatar
                                    url={imgByProduct.get(l.productId)}
                                    name={l.name}
                                    onZoom={setZoomUrl}
                                  />
                                  <div className="min-w-0">
                                    <div className="font-medium">{l.name}</div>
                                    <div className="text-xs text-gray-500">
                                      {l.sku}
                                      {l.priceMissing && (
                                        <span className="ml-1 text-amber-700">· preț lipsă</span>
                                      )}
                                      {l.stock != null && (
                                        <span
                                          className={
                                            l.qty > l.stock
                                              ? 'ml-1 text-red-600'
                                              : 'ml-1 text-gray-400'
                                          }
                                        >
                                          · stoc {l.stock}
                                          {l.qty > l.stock ? ' (depășit)' : ''}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-2 pr-3 text-right">
                                <input
                                  type="number"
                                  min={1}
                                  className="w-16 rounded border px-2 py-1 text-right"
                                  value={l.qty}
                                  disabled={!editable}
                                  onChange={(e) =>
                                    updateLine(idx, { qty: Math.max(1, Number(e.target.value)) })
                                  }
                                />
                              </td>
                              <td className="py-2 pr-3 text-right">
                                <input
                                  type="number"
                                  step="0.01"
                                  className={`w-24 rounded border px-2 py-1 text-right ${
                                    !isAdmin ? 'cursor-not-allowed bg-gray-100 text-gray-500' : ''
                                  }`}
                                  value={l.listPrice}
                                  disabled={!editable || !isAdmin}
                                  title={
                                    !isAdmin
                                      ? 'Doar adminul poate modifica prețul. Poți ajusta doar discountul.'
                                      : undefined
                                  }
                                  onChange={(e) =>
                                    updateLine(idx, { listPrice: Number(e.target.value) })
                                  }
                                />
                              </td>
                              <td className="py-2 pr-3 text-right">
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  className="w-16 rounded border px-2 py-1 text-right"
                                  value={l.lineDiscountPct ?? ''}
                                  placeholder={String(orderDiscountPct)}
                                  disabled={!editable}
                                  onChange={(e) =>
                                    updateLine(idx, {
                                      lineDiscountPct:
                                        e.target.value === '' ? null : Number(e.target.value),
                                    })
                                  }
                                />
                              </td>
                              <td className="py-2 pr-3 text-right">
                                {fmt(computed.rows[idx]?.eff ?? 0)}
                              </td>
                              <td className="py-2 pr-3 text-right font-semibold">
                                {fmt(computed.rows[idx]?.lineNoVat ?? 0)}
                              </td>
                              <td className="py-2 pr-3">{l.vatRate}%</td>
                              {editable && (
                                <td className="py-2">
                                  <button className="text-red-600" onClick={() => removeLine(idx)}>
                                    ✕
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-sm text-gray-600">
                      Discount general comandă (%)
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className="w-full rounded-md border border-gray-400 bg-white px-3 py-2 shadow-sm"
                      value={orderDiscountPct}
                      disabled={!editable}
                      onChange={(e) =>
                        setOrderDiscountPct(Math.min(100, Math.max(0, Number(e.target.value))))
                      }
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm text-gray-600">Observații</span>
                    <input
                      className="w-full rounded-md border border-gray-400 bg-white px-3 py-2 shadow-sm"
                      value={notes}
                      disabled={!editable}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </label>
                  {/* Ramburs — excepție rară, de aceea o bifă, nu o listă cu implicit. */}
                  <label className="flex cursor-pointer items-start gap-2 sm:col-span-2">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={cod}
                      disabled={!editable}
                      onChange={(e) => setCod(e.target.checked)}
                    />
                    <span className="text-sm">
                      <span className="font-medium">Plată la livrare (ramburs)</span>
                      <span className="block text-xs text-b4l-text-muted">
                        Clientul plătește curierului. Comanda pleacă în ExpertAccounts cu{' '}
                        <code>terms = COD</code>. Implicit, plata e pe factură, la termenul din fișa
                        clientului.
                      </span>
                    </span>
                  </label>
                </div>
              </Card>
            </>
          ) : (
            <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch">
              {/* Rând 1: Client | Produse comandate | Rezumat (întinse pe înălțimea rândului) */}
              <Card>
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
                    <Building2 size={18} />
                  </span>
                  <h2 className="text-base font-semibold tracking-tight">Client</h2>
                </div>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Client</dt>
                    <dd className="text-right font-medium">{clientName}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Punct de lucru</dt>
                    <dd className="text-right font-medium">{punctLucruLabel}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Tip preț</dt>
                    <dd className="text-right font-medium">
                      {hasClientPrices ? 'Preț client' : 'Preț listă'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Agent</dt>
                    <dd className="text-right font-medium">{detail?.client?.agentName || '—'}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Termen de plată</dt>
                    <dd className="text-right font-medium">
                      {detail?.client?.paymTermDays != null
                        ? `${detail.client.paymTermDays} zile`
                        : '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Ultima comandă</dt>
                    <dd className="text-right font-medium">
                      {lastOrderAt ? new Date(lastOrderAt).toLocaleDateString('ro-RO') : '—'}
                    </dd>
                  </div>
                </dl>
              </Card>

              <Card>
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
                    <Package size={18} />
                  </span>
                  <h2 className="text-base font-semibold tracking-tight">Produse comandate</h2>
                </div>
                {lines.length === 0 ? (
                  <p className="text-sm text-gray-500">Nicio linie.</p>
                ) : (
                  <div className="divide-y">
                    {lines.map((l, idx) => (
                      <div key={l.productId} className="flex items-center gap-3 py-2">
                        <ProductAvatar
                          url={imgByProduct.get(l.productId)}
                          name={l.name}
                          className="h-10 w-10"
                          onZoom={setZoomUrl}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium">{l.name}</div>
                          <div className="text-xs text-gray-500">
                            {l.sku} · {l.qty} buc × {fmt(computed.rows[idx]?.eff ?? 0)} lei
                          </div>
                          {(() => {
                            const st = stockByProduct.get(l.productId)
                            if (st == null) return null
                            const insuf = l.qty > st
                            return (
                              <div
                                className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                  insuf
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-emerald-100 text-emerald-700'
                                }`}
                              >
                                Stoc {st}
                                {insuf ? ' · insuficient' : ''}
                              </div>
                            )
                          })()}
                        </div>
                        <div className="shrink-0 text-right text-sm font-semibold">
                          {fmt(computed.rows[idx]?.lineNoVat ?? 0)} lei
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex justify-between border-t pt-3 text-sm">
                  <span className="text-gray-500">{lines.length} produse</span>
                  <span className="font-semibold">Total fără TVA {fmt(computed.noVat)} lei</span>
                </div>

                {/*
                  Din ce moment e stocul de mai sus. Fără asta, o etichetă „insuficient" nu se
                  poate deosebi de una învechită — vezi comentariul de la `stocLaData`.
                */}
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500">
                  <span>
                    {stocLaData
                      ? `Stoc citit din ExpertAccounts la ${new Date(stocLaData).toLocaleString('ro-RO')}`
                      : 'Momentul citirii stocului nu se cunoaște.'}
                  </span>
                  <span className="text-gray-300">·</span>
                  <span>doar gestiunea Depozit</span>
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => resyncStoc.mutate()}
                      disabled={resyncStoc.isPending}
                      className="text-b4l-blue-dark underline disabled:opacity-50"
                    >
                      {resyncStoc.isPending ? 'Citesc…' : 'Recitește acum'}
                    </button>
                  )}
                </div>
              </Card>

              <Card className="hidden lg:block">
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
                    <ShoppingCart size={18} />
                  </span>
                  <h2 className="text-base font-semibold tracking-tight">Rezumat comandă</h2>
                </div>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Subtotal fără TVA</dt>
                    <dd className="font-medium">{fmt(computed.noVat)} lei</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">TVA</dt>
                    <dd className="font-medium">{fmt(computed.vat)} lei</dd>
                  </div>
                  <div className="mt-1 flex items-center justify-between border-t pt-2">
                    <dt className="font-semibold">Total cu TVA</dt>
                    <dd className="text-lg font-bold text-b4l-blue">{fmt(computed.withVat)} lei</dd>
                  </div>
                </dl>
                {willNeedApproval && editable && (
                  <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Discountul depășește pragul — comanda va cere aprobare.
                  </div>
                )}
              </Card>

              {/* Rând 2: Livrare & Observații | Checklist | Acțiuni */}
              <Card>
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
                    <Truck size={18} />
                  </span>
                  <h2 className="text-base font-semibold tracking-tight">Livrare & Observații</h2>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-b4l-text-muted">
                      Punct de livrare
                    </div>
                    <div className="font-medium">{livrareAdresa}</div>
                    {livrareContact && <div className="text-gray-500">{livrareContact}</div>}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-b4l-text-muted">
                      Observații
                    </div>
                    <div className="font-medium">{notes || '—'}</div>
                  </div>
                  {/* Se arata doar cand e ramburs: fiind exceptia, absenta ei nu spune nimic. */}
                  {cod && (
                    <div className="rounded border border-b4l-amber bg-b4l-amber-light px-2 py-1.5 text-b4l-amber-dark">
                      <span className="font-semibold">Plată la livrare (ramburs)</span> — pleacă în
                      EA cu <code>terms = COD</code>
                    </div>
                  )}
                </div>
              </Card>

              <Card>
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
                    <ClipboardCheck size={18} />
                  </span>
                  <h2 className="text-base font-semibold tracking-tight">
                    Checklist înainte de trimitere
                  </h2>
                </div>
                <div className="space-y-1.5">
                  {CHECK_ITEMS.map((item) => (
                    <label key={item} className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checks.has(item)}
                        onChange={() =>
                          setChecks((prev) => {
                            const next = new Set(prev)
                            if (next.has(item)) next.delete(item)
                            else next.add(item)
                            return next
                          })
                        }
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>
                {checks.size === CHECK_ITEMS.length && (
                  <div className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    <CheckCircle2 size={16} /> Totul e pregătit pentru trimitere.
                  </div>
                )}
              </Card>

              {/* Acțiunile au urcat în antet; aici, traseul comenzii (jurnalul ei). */}
              <Card>
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
                    <Zap size={18} />
                  </span>
                  <h2 className="text-base font-semibold tracking-tight">Traseu</h2>
                </div>
                <Traseu
                  pasi={pasiTraseu(detail?.auditEvents ?? [])}
                  gol="Nicio schimbare înregistrată încă."
                />
              </Card>
            </div>
          )}
        </div>

        {/* Coloana dreaptă (desktop, DOAR editare): rezumat + politică + acțiuni */}
        {mode === 'edit' && (
          <div className="hidden space-y-4 lg:sticky lg:top-4 lg:block">
            <Card>
              <div className="mb-3 flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
                  <ShoppingCart size={18} />
                </span>
                <h2 className="text-base font-semibold tracking-tight">Rezumat comandă</h2>
              </div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Subtotal fără TVA</dt>
                  <dd className="font-medium">{fmt(computed.noVat)} lei</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">TVA</dt>
                  <dd className="font-medium">{fmt(computed.vat)} lei</dd>
                </div>
                <div className="mt-1 flex items-center justify-between border-t pt-2">
                  <dt className="font-semibold">Total cu TVA</dt>
                  <dd className="text-lg font-bold text-b4l-blue">{fmt(computed.withVat)} lei</dd>
                </div>
              </dl>
              {willNeedApproval && editable && (
                <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Discountul depășește pragul — comanda va cere aprobare.
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">{renderActions()}</div>
            </Card>

            {mode === 'edit' && editable && (
              <Card>
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
                    <Percent size={18} />
                  </span>
                  <h2 className="text-base font-semibold tracking-tight">Politică discount</h2>
                </div>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Prag maxim</dt>
                    <dd className="font-medium">{threshold}%</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Discount curent (max)</dt>
                    <dd className="font-medium">{computed.maxDisc}%</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-500">Stare</dt>
                    <dd>
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${discState.cls}`}>
                        {discState.label}
                      </span>
                    </dd>
                  </div>
                </dl>
                <p className="mt-2 text-xs text-gray-500">
                  Discounturile peste prag necesită aprobarea unui admin.
                </p>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Bară totaluri sticky jos (mobil) */}
      <div
        ref={bottomBarRef}
        className="fixed inset-x-0 bottom-0 z-10 border-t bg-white p-3 shadow-lg lg:hidden"
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <span className="text-gray-500">Fără TVA</span> <b>{fmt(computed.noVat)}</b>
            <span className="mx-2 text-gray-300">|</span>
            <span className="text-gray-500">TVA</span> <b>{fmt(computed.vat)}</b>
            <span className="mx-2 text-gray-300">|</span>
            <span className="text-gray-500">Total</span>{' '}
            <b className="text-base">{fmt(computed.withVat)} lei</b>
            {willNeedApproval && editable && (
              <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                va cere aprobare
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">{renderActions()}</div>
        </div>
      </div>

      {/* Lightbox poză produs (tap oriunde închide) */}
      {zoomUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setZoomUrl(null)}
        >
          <img
            src={zoomUrl}
            alt=""
            className="max-h-[85vh] max-w-full rounded-lg bg-white object-contain shadow-2xl"
          />
          <button
            type="button"
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-lg text-gray-700 shadow"
            onClick={() => setZoomUrl(null)}
          >
            ✕
          </button>
        </div>
      )}

      {spvViewer && (
        <SpvInvoiceViewer
          inboundId={spvViewer.inboundId}
          orderId={orderId ?? undefined}
          docId={spvViewer.docId}
          onClose={() => setSpvViewer(null)}
        />
      )}
      <AddBranchModal
        open={addBranchOpen}
        clientId={clientId}
        onClose={() => setAddBranchOpen(false)}
        onCreated={async (id) => {
          setAddBranchOpen(false)
          await qc.invalidateQueries({ queryKey: ['client-branches', clientId] })
          setBranchId(id)
        }}
      />

      {/*
        Confirmarea stergerii sta INTR-UN MODAL, nu in `window.confirm`: dialogul de browser
        poate fi suprimat de Chrome („Prevent this page from creating additional dialogs")
        dupa cateva dialoguri la rand, iar atunci avertismentul nu mai apare deloc.
        Avertismentul se leaga de EXISTENTA documentului in EA (`eaDocId`), nu de status:
        la un esec partial de trimitere statusul poate ramane altul desi documentul s-a
        creat deja in ERP (cazul CMD-2026-0086).
      */}
      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Ștergi comanda ${code}?`}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Renunță
            </Button>
            <Button
              variant="danger"
              loading={deleteMut.isPending}
              onClick={() => {
                setConfirmDelete(false)
                deleteMut.mutate()
              }}
            >
              Șterge din CRM
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          {detail?.eaDocId ? (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-red-800">
              <div className="font-semibold">
                Comanda a fost trimisă în ExpertAccounts ca documentul {detail.eaDocId}.
              </div>
              <div className="mt-1">
                Ștergerea din CRM <b>NU</b> îl anulează acolo — documentul rămâne în ExpertAccounts
                și trebuie anulat separat.
              </div>
            </div>
          ) : status === 'eroare_ea' || detail?.eaError ? (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-red-800">
              Trimiterea în ExpertAccounts a eșuat, dar comanda poate exista totuși acolo. Verifică
              în EA înainte să o ștergi: ștergerea din CRM nu o anulează acolo.
            </div>
          ) : status === 'aprobata' ? (
            <div className="rounded border border-b4l-amber bg-b4l-amber-light p-3 text-b4l-amber-dark">
              Comanda e aprobată. Dacă a ajuns în ExpertAccounts, nu se anulează acolo.
            </div>
          ) : null}
          <p className="text-b4l-text-muted">Acțiunea nu se poate reface.</p>
        </div>
      </Modal>
    </div>
  )
}
